import React, { useEffect, useState, useRef } from "react";
import { Col, Container, Row, Toast } from "react-bootstrap";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import "./SelectButton";
import { useDispatch, useSelector } from "react-redux";
import { postProfile } from "../../store/thunks/post-profile-thunk";
import { selectProfile } from "../../store/slices/profile-slice";
import Spinner from "react-bootstrap/Spinner";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import Button from "react-bootstrap/Button";
import "./Profile.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchProfile } from "../../store/thunks/fetch-profile-thunk";
import { scroller } from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheckCircle,
    faIdCard,
    faUserCircle,
    faGraduationCap,
    faBriefcase,
    faInfoCircle,
    faExclamationTriangle,
    faArrowRight,
    faUserCheck,
    faRightToBracket
} from "@fortawesome/free-solid-svg-icons";
import { ArrowLeft } from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import Card from "react-bootstrap/Card";
import { selectUploadFiles } from "../../store/slices/upload-slice";
import { resetUploadFile } from "../../store/slices/upload-slice";
import { selectDeleteUploadFiles } from "../../store/slices/delete-upload-slice";
import { resetDeleteUploadFile } from "../../store/slices/delete-upload-slice";
import { selectProfileStatus } from "../../store/slices/profile-status-slice";
import { formatDateToInputFormat } from "./util/helpers.js";
import { PersonalInformation } from "./PersonalInformation.js";
import { DriverLicense } from "./DriverLicense.js";
import { EducationDetails } from "./EducationDetails.js";
import { EmploymentHistory } from "./EmploymentHistory.js";
import { AdditionalInfo } from "./AdditionalInfo.js";
import moment from "moment";
import { fetchProfileStatus } from "../../store/thunks/fetch-profile-status-thunk";
import { selectSignIn } from "../../store/slices/signed-in-slice";

export const ProfileCard = () => {

    const dispatch = useDispatch();
    const [shouldShake, setShouldShake] = useState(false);
    const profile = useSelector(selectProfile);
    const profileStatus = useSelector((state) => state.profile.status);
    const profileError = useSelector((state) => state.profile.error);
    const [submit, setSubmit] = useState(false);
    const navigate = useNavigate();

    const [step, setStep] = useState(1);

    const uploadPromises = useSelector(selectUploadFiles);
    const deletePromises = useSelector(selectDeleteUploadFiles);
    const profileComplete = useSelector(selectProfileStatus);
    const isSignedIn = useSelector(selectSignIn);

    useEffect(() => {
        if(isSignedIn){
            dispatch(fetchProfileStatus());
        }
    }, [dispatch, isSignedIn]);

     // Choose what to scroll to when loading in to the page
    const ref = useRef(null);

    // Extracts pathname property(key) from an object
    const { pathname } = useLocation();

    // Automatically scrolls to top whenever pathname changes
    useEffect(() => {
        // scroll to component in profilecard.js
        // ref.current?.scrollIntoView({ behavior: "smooth" });
        // scroll to top of the page
        window.scrollTo(0, 0)
    }, [pathname]);


    const arePersonalInfoFieldsFilled = (values) => {
        return (
            values.firstName &&
            values.lastName &&
            values.phoneNumber &&
            values.dob &&
            values.gender &&
            values.addressLine1 &&
            values.city &&
            values.state &&
            values.zipcode &&
            values.gender
        );
    };

    const areDriverLicenseFieldsFilled = (values) => {
        if (values.hasDriverLicense === "No") {
            return true;
        }

        if (values.hasDriverLicense === "") {
            return false;
        }

        return (
            values.driverLicenseNumber &&
            values.driverLicenseClass &&
            values.driverLicenseState &&
            values.driverLicenseExpirationDate &&
            values.driverLicenseType
        );
    };

    function areFieldsFilled(fieldNames, values) {
        return fieldNames.every((fieldName) => Boolean(values[fieldName]));
    }

    const areEducationFieldsFilled = (values) => {
        const fields = {
            attendedHighSchool: [
                "highSchoolName",
                "highSchoolAddress",
                "education",
                "highSchoolStart",
                "highSchoolEnd",
            ],
            attendedCollege1: [
                "collegeName1",
                "collegeAddress1",
                "degree1",
                "major1",
                "collegeStart1",
                "collegeEnd1",
                "collegeCreditHours1",
            ],
            attendedCollege2: [
                "collegeName2",
                "collegeAddress2",
                "degree2",
                "major2",
                "collegeStart2",
                "collegeEnd2",
                "collegeCreditHours2",
            ],
            attendedTechSchool: [
                "techSchoolName1",
                "techSchoolAddress1",
                "techDegree1",
                "techMajor1",
                "techStart1",
                "techEnd1",
                "techCreditHours1",
            ],
        };

        for (const [attended, requiredFields] of Object.entries(fields)) {
            if (values[attended] === "") return false;
            if (
                values[attended] === "Yes" &&
                !areFieldsFilled(requiredFields, values)
            )
                return false;
        }

        return true;
    };

    const areAdditionalFieldsFilled = (values) => {
        const fields = {
            felony: ["felonyDate", "felonyReason"],
            misdemeanor: ["misdemeanorDate", "misdemeanorReason"],
            physical: ["physicalName1", "physicalReason1"],
            relations: [
                "relationsName1",
                "relationsDepartment1",
                "relationsRelationship1",
            ],
        };

        for (const [attended, requiredFields] of Object.entries(fields)) {
            if (values[attended] === "") return false;
            if (
                values[attended] === "Yes" &&
                !areFieldsFilled(requiredFields, values)
            )
                return false;
        }

        return true;
    };

    const numEmployerFields = 10;

    let employmentFieldsMap = {};

    let areEmploymentFieldsFilled = (values) => {
        for (let i = 1; i <= numEmployerFields; i++) {
            if (
                !values[`employer${i}`] &&
                !values[`employerAddress${i}`] &&
                !values[`employerDuties${i}`] &&
                !values[`employerJobTitle${i}`] &&
                !values[`employerPhone${i}`] &&
                !values[`employerReason${i}`] &&
                !values[`employerSupervisor${i}`] &&
                !values[`employerEnd${i}`] &&
                !values[`employerStart${i}`]
            ) {
                if (
                    i > 0 &&
                    values[`employer${i - 1}`] &&
                    values[`employerAddress${i - 1}`] &&
                    values[`employerDuties${i - 1}`] &&
                    values[`employerJobTitle${i - 1}`] &&
                    values[`employerPhone${i - 1}`] &&
                    values[`employerReason${i - 1}`] &&
                    values[`employerSupervisor${i - 1}`] &&
                    values[`employerEnd${i - 1}`] &&
                    values[`employerStart${i - 1}`]
                ) {
                    return true;
                }

                break;
            }
            if (
                i === 10 &&
                values[`employer${i}`] &&
                values[`employerAddress${i}`] &&
                values[`employerDuties${i}`] &&
                values[`employerJobTitle${i}`] &&
                values[`employerPhone${i}`] &&
                values[`employerReason${i}`] &&
                values[`employerSupervisor${i}`] &&
                values[`employerEnd${i}`] &&
                values[`employerStart${i}`]
            ) {
                return true;
            }
            employmentFieldsMap[`employer${i}`] = `Employer Name ${i}`;
            employmentFieldsMap[`employerAddress${i}`] =
                `Employment Address ${i}`;
            employmentFieldsMap[`employerDuties${i}`] =
                `Employment Duties ${i}`;
            employmentFieldsMap[`employerJobTitle${i}`] =
                `Employment Job Title ${i}`;
            employmentFieldsMap[`employerPhone${i}`] = `Employment Phone ${i}`;
            employmentFieldsMap[`employerReason${i}`] =
                `Employment Reason ${i}`;
            employmentFieldsMap[`employerSupervisor${i}`] =
                `Employment Supervisor ${i}`;
            employmentFieldsMap[`employerEnd${i}`] = `Employment End Date ${i}`;
            employmentFieldsMap[`employerStart${i}`] =
                `Employment Start Date ${i}`;

            if (
                !(
                    values[`employer${i}`] &&
                    values[`employerAddress${i}`] &&
                    values[`employerDuties${i}`] &&
                    values[`employerJobTitle${i}`] &&
                    values[`employerPhone${i}`] &&
                    values[`employerReason${i}`] &&
                    values[`employerSupervisor${i}`] &&
                    values[`employerEnd${i}`] &&
                    values[`employerStart${i}`]
                )
            ) {
                return false;
            }
        }

        return false;
    };


    const anyErrors = (errors) => {
        return errors.length !== 0;
    };



    const handleFieldClick = (fieldName) => () => {
        const stepToGoTo = STEP_FIELD_MAPPING[fieldName];
        goToStep(stepToGoTo);

        setTimeout(() => {
            const windowHeight =
                typeof window !== "undefined" ? window.innerHeight : 0;
            scroller.scrollTo(fieldName, {
                duration: 0,
                delay: 0,
                smooth: "easeInOutQuart",
                offset: -windowHeight / 2,
            });
        }, 300);
    };

    function checkFields(values, fieldsToCheck) {
        const missingFields = Object.keys(fieldsToCheck).filter((fieldName) =>
            isNullOrEmpty(values[fieldName]),
        );

        return missingFields.length > 0 ? missingFields : true;
    }

    function isNullOrEmpty(variable) {
        return variable === null || variable === undefined || variable === "";
    }

    function areAllFieldsFilled(values) {
        return (
            arePersonalInfoFieldsFilled(values) &&
            areDriverLicenseFieldsFilled(values) &&
            areEducationFieldsFilled(values) &&
            areEmploymentFieldsFilled(values) &&
            areAdditionalFieldsFilled(values)
        );
    }
    const personalInfoFieldsMap = {
        firstName: "First Name",
        lastName: "Last Name",
        phoneNumber: "Phone Number",
        dob: "Date of Birth",
        gender: "Gender",
        addressLine1: "Address Line 1",
        city: "City",
        state: "State",
        zipcode: "Zipcode",
    };

    const driverLicenseFieldsMap = {
        driverLicenseNumber: "Driver License Number",
        driverLicenseClass: "Driver License Class",
        driverLicenseState: "Driver License State",
        driverLicenseExpirationDate: "Driver License Expiration Date",
        driverLicenseType: "Driver License Type",
    };
    const highSchoolFieldsMap = {
        highSchoolName: "High School Name",
        highSchoolAddress: "High School Address",
        highSchoolStart: "High School Start",
        highSchoolEnd: "High School End",
        education: "High School Diploma/GED",
    };
    const college1FieldsMap = {
        collegeName1: "College Name 1",
        collegeStart1: "College Start 1",
        collegeEnd1: "College End 1",
        collegeAddress1: "College Address 1",
        degree1: "College Degree 1",
        major1: "College Major 1",
        minor1: "College Minor 1",
        collegeCreditHours1: "College Credit Hours 1",
    };
    const college2FieldsMap = {
        collegeName2: "College Name 2",
        collegeStart2: "College Start 2",
        collegeEnd2: "College End 2",
        collegeAddress2: "College Address 2",
        degree2: "College Degree 2",
        major2: "College Major 2",
        minor2: "College Minor 2",
        collegeCreditHours2: "College Credit Hours 2",
    };

    const techSchoolFieldsMap = {
        techSchoolName1: "Tech/Voc School Name",
        techSchoolAddress1: "Tech/Voc School Address",
        techDegree1: "Tech/Voc Degree",
        techMajor1: "Tech/Voc Major",
        techMinor1: "Tech/Voc Minor",
        techStart1: "Tech/Voc Start",
        techEnd1: "Tech/Voc End",
    };

    const felonyFieldsMap = {
        felonyDate: "Felony Date",
        felonyReason: "Felony Reason",
    };
    const misdemeanorFieldsMap = {
        misdemeanorDate: "Misdemeanor Date",
        misdemeanorReason: "Misdemeanor Reason",
    };
    const physical1FieldsMap = {
        physicalName1: "Physical Condition 1",
        physicalReason1: "Physical Reason 1",
    };

    const physical2FieldsMap = {
        physicalName2: "Physical Condition 2",
        physicalReason2: "Physical Reason 2",
    };

    const relations1FieldsMap = {
        relationsName1: "Relations Name 1",
        relationsDepartment1: "Relations Department 1",
        relationsRelationship1: "Relationship 1",
    };

    const relations2FieldsMap = {
        relationsName2: "Relations Name 2",
        relationsDepartment2: "Relations Department 2",
        relationsRelationship2: "Relationship 2",
    };

    const STEP_FIELD_MAPPING = {
        firstName: 1,
        lastName: 1,
        phoneNumber: 1,
        dob: 1,
        gender: 1,
        addressLine1: 1,
        city: 1,
        state: 1,
        zipcode: 1,
        driverLicenseNumber: 2,
        driverLicenseClass: 2,
        driverLicenseState: 2,
        driverLicenseExpirationDate: 2,
        driverLicenseType: 2,
        highSchoolName: 3,
        highSchoolAddress: 3,
        highSchoolStart: 3,
        highSchoolEnd: 3,
        education: 3,
        collegeName1: 3,
        collegeStart1: 3,
        collegeEnd1: 3,
        collegeAddress1: 3,
        degree1: 3,
        major1: 3,
        minor1: 3,
        collegeCreditHours1: 3,
        collegeName2: 3,
        collegeStart2: 3,
        collegeEnd2: 3,
        collegeAddress2: 3,
        degree2: 3,
        major2: 3,
        minor2: 3,
        collegeCreditHours2: 3,
        techSchoolName1: 3,
        techSchoolAddress1: 3,
        techDegree1: 3,
        techMajor1: 3,
        techMinor1: 3,
        techStart1: 3,
        techEnd1: 3,
        techCreditHours1: 3,
        employer1: 4,
        employerAddress1: 4,
        employerDuties1: 4,
        employerJobTitle1: 4,
        employerPhone1: 4,
        employerReason1: 4,
        employerSupervisor1: 4,
        employerEnd1: 4,
        employerStart1: 4,
        employer2: 4,
        employerAddress2: 4,
        employerDuties2: 4,
        employerJobTitle2: 4,
        employerPhone2: 4,
        employerReason2: 4,
        employerSupervisor2: 4,
        employerEnd2: 4,
        employerStart2: 4,
        employer3: 4,
        employerAddress3: 4,
        employerDuties3: 4,
        employerJobTitle3: 4,
        employerPhone3: 4,
        employerReason3: 4,
        employerSupervisor3: 4,
        employerEnd3: 4,
        employerStart3: 4,
        employer4: 4,
        employerAddress4: 4,
        employerDuties4: 4,
        employerJobTitle4: 4,
        employerPhone4: 4,
        employerReason4: 4,
        employerSupervisor4: 4,
        employerEnd4: 4,
        employerStart4: 4,
        employer5: 4,
        employerAddress5: 4,
        employerDuties5: 4,
        employerJobTitle5: 4,
        employerPhone5: 4,
        employerReason5: 4,
        employerSupervisor5: 4,
        employerEnd5: 4,
        employerStart5: 4,
        employer6: 4,
        employerAddress6: 4,
        employerDuties6: 4,
        employerJobTitle6: 4,
        employerPhone6: 4,
        employerReason6: 4,
        employerSupervisor6: 4,
        employerEnd6: 4,
        employerStart6: 4,
        employer7: 4,
        employerAddress7: 4,
        employerDuties7: 4,
        employerJobTitle7: 4,
        employerPhone7: 4,
        employerReason7: 4,
        employerSupervisor7: 4,
        employerEnd7: 4,
        employerStart7: 4,
        employer8: 4,
        employerAddress8: 4,
        employerDuties8: 4,
        employerJobTitle8: 4,
        employerPhone8: 4,
        employerReason8: 4,
        employerSupervisor8: 4,
        employerEnd8: 4,
        employerStart8: 4,
        employer9: 4,
        employerAddress9: 4,
        employerDuties9: 4,
        employerJobTitle9: 4,
        employerPhone9: 4,
        employerReason9: 4,
        employerSupervisor9: 4,
        employerEnd9: 4,
        employerStart9: 4,
        employer10: 4,
        employerAddress10: 4,
        employerDuties10: 4,
        employerJobTitle10: 4,
        employerPhone10: 4,
        employerReason10: 4,
        employerSupervisor10: 4,
        employerEnd10: 4,
        employerStart10: 4,
        felonyDate: 5,
        felonyReason: 5,
        misdemeanorDate: 5,
        misdemeanorReason: 5,
        physicalName1: 5,
        physicalReason1: 5,
        physicalName2: 5,
        physicalReason2: 5,
        physicalName3: 5,
        physicalReason3: 5,
        relationsName1: 5,
        relationsDepartment1: 5,
        relationsRelationship1: 5,
        relationsName2: 5,
        relationsDepartment2: 5,
        relationsRelationship2: 5,
    };
    const Step = () => {
        switch (step) {
            case 1:
                return <PersonalInformation />;
            case 2:
                return <DriverLicense />;
            case 3:
                return <EducationDetails />;
            case 4:
                return <EmploymentHistory />;
            case 5:
                return <AdditionalInfo />;

            default:
                return <PersonalInformation />;
        }
    };

    const prevStep = () => {
        setStep(step - 1);
    };

    const nextStep = () => {
        setStep(step + 1);
    };

    const goToStep = (num) => {
        setStep(num);
    };

    const signInValidationSchema = Yup.object({
        firstName: Yup.string()
            .required("* First Name is Required")
            .trim()
            .max(100, "First Name is too long. 100 Characters Max"),
        middleName: Yup.string().max(
            100,
            "Middle Name is too long. 100 Characters Max",
        ),
        lastName: Yup.string()
            .required("* Last Name is Required")
            .max(100, "Last Name is too long. 100 Characters Max"),
        suffix: Yup.string().optional(),
        // phoneNumber: Yup.string().required("* Phone Number is Required"),
        phoneNumber: Yup.string()
            .required("* Phone Number is Required")
            .test(
                "phoneNumber-logging",
                "* Phone Number not possible",
                (value) => {
                    const isPossible = !value || isPossiblePhoneNumber(value);
                    return isPossible;
                },
            ),

        dob: Yup.date()
            .required("is required")
            .max(moment().endOf('day'), "Date of birth cannot be set in the future"),
        gender: Yup.string().required("is Required"),
        addressLine1: Yup.string()
            .required("is Required")
            .max(200, "Address Line 1 is too long. 200 Characters Max"),
        addressLine2: Yup.string().max(
            200,
            "Address Line 2 is too long. 200 Characters Max",
        ),
        city: Yup.string()
            .required("is Required")
            .max(100, "city is too long. 100 Characters Max"),
        state: Yup.string().required("is Required"),

        zipcode: Yup.string()
            .required("is Required")
            .max(75, "Zipcode is too long. 75 Characters Max"),
        census: Yup.string()
            .optional()
            .trim()
            .test(
                "isLengthValid",
                "Census be at least 6 characters long.",
                (val) => {
                    if (val && val.length === 6) {
                        return val.length === 6 && /^X{4}\d{2}$/.test(val);
                    } else if (val && val.length === 7) {
                        return (
                            val.length <= 7 &&
                            val.replace(/_/g, "").length === 7
                        );
                    }
                    return true;
                },
            ),
        nationality: Yup.string()
            .optional()
            .max(75, "Nationality is too long. 75 Characters Max"),
        rifDate: Yup.date().max(new Date(4000,12,31), "Lay off Date too large. Max: 12/31/4000)"),
        hasDriverLicense: Yup.string().optional(),
        driverLicenseNumber: Yup.string()
            .trim()
            .max(30, "Drivers License is too long. 30 Characters Max"),
        driverLicenseClass: Yup.string()
            .trim()
            .when("driverLicenseNumber", {
                is: (value) => value && value.length !== 0,
                then: (schema) => schema.optional(),
                otherwise: (schema) => schema,
            }),
        driverLicenseState: Yup.string()
            .trim()
            .when("driverLicenseNumber", {
                is: (value) => value && value.length !== 0,
                then: (schema) => schema.optional(),
                otherwise: (schema) => schema,
            }),
        driverLicenseExpirationDate: Yup.date().optional().max(new Date(4000,12,31), "Driver License Expiration Date too large. Max: 12/31/4000)"),
        driverLicenseType: Yup.string().optional(),
        navajoTribeMember: Yup.string().optional(),
        attendedHighSchool: Yup.string().optional(),
        highSchoolName: Yup.string()
            .optional()
            .max(150, "High School Name is too long. 150 Characters Max"),
        education: Yup.string().optional(),
        highSchoolAddress: Yup.string()
            .optional()
            .max(200, "High School Address is too long. 200 Characters Max"),
        highSchoolStart: Yup.date().max(new Date(4000,12,31), "High School Start Date too large. Max: 12/31/4000)").optional(),
        highSchoolEnd: Yup.date()
            .max(new Date(4000,12,31), "High School End Date too large Max: 12/31/4000)")
            .optional()
            .min(
                Yup.ref("highSchoolStart"),
                "End date can't be before start date",
            ),
        currentlyEmployed: Yup.string().optional(),
        attendedCollege1: Yup.string().optional(),
        collegeName1: Yup.string()
            .optional()
            .max(150, "College 1 Name is too long. 150 Characters Max"),
        collegeAddress1: Yup.string()
            .optional()
            .max(200, "College 1 Address is too long. 200 Characters Max"),
        collegeCreditHours1: Yup.string()
            .transform((value, originalValue) =>
                /^\s*0\s*$/.test(originalValue) ? "0" : value,
            )
            .matches(/^[0-9]*$/, "Only numbers allowed.")
            .max(10, "College 1 Name is too long. 10 Characters Max"),
        /* collegeCreditHours1: Yup.string()
            .optional()
            .matches(/^[0-9]*$/, "Only numbers allowed.")
            .max(10, "College 2 Name is too long. 10 Characters Max"),*/

        degree1: Yup.string().optional(),
        major1: Yup.string()
            .optional()
            .max(100, "College 1 Major is too long. 100 Characters Max"),
        minor1: Yup.string()
            .optional()
            .max(100, "College 1 Minor is too long. 100 Characters Max"),
        collegeStart1: Yup.date().max(new Date(4000,12,31), "College Start Date too large. Max: 12/31/4000)").optional(),
        collegeEnd1: Yup.date()
            .max(new Date(4000,12,31), "College End Date too large Max: 12/31/4000)")
            .optional()
            .min(
                Yup.ref("collegeStart1"),
                "End date can't be before start date",
            ),
        attendedCollege2: Yup.string().optional(),
        collegeName2: Yup.string()
            .optional()
            .max(150, "College 2 Name is too long. 150 Characters Max"),
        collegeAddress2: Yup.string()
            .optional()
            .max(200, "College 2 Address is too long. 200 Characters Max"),
        degree2: Yup.string().optional(),
        major2: Yup.string()
            .optional()
            .max(100, "College 2 Major is too long. 100 Characters Max"),
        minor2: Yup.string()
            .optional()
            .max(100, "College 2 Minor is too long. 100 Characters Max"),
        collegeStart2: Yup.date().max(new Date(4000,12,31), "College 2 Start Date too large. Max: 12/31/4000)").optional(),
        collegeEnd2: Yup.date()
            .max(new Date(4000,12,31), "College 2 End Date too large Max: 12/31/4000)")
            .optional()
            .min(
                Yup.ref("collegeStart2"),
                "End date can't be before start date",
            ),
        /*   collegeCreditHours2: Yup.string().matches(
            /^(0|[1-9][0-9]{0,9})$/,
            "Must be a positive number, up to 10 digits",
        ),*/
        collegeCreditHours2: Yup.string()
            .transform((value, originalValue) =>
                /^\s*0\s*$/.test(originalValue) ? "0" : value,
            )
            .matches(/^[0-9]*$/, "Only numbers allowed.")
            .max(10, "College 2 Name is too long. 10 Characters Max"),
        /*   collegeCreditHours2: Yup.string()
            .optional()
            .matches(/^[0-9]*$/, "Only numbers allowed.")
            .max(10, "College 2 Name is too long. 10 Characters Max"),*/
        attendedTechSchool: Yup.string().optional(),
        techSchoolName1: Yup.string()
            .optional()
            .max(150, "College 2 Name is too long. 150 Characters Max"),
        techSchoolAddress1: Yup.string()
            .optional()
            .max(200, "College 2 Address is too long. 200 Characters Max"),
        techDegree1: Yup.string().optional(),
        techMajor1: Yup.string()
            .optional()
            .max(100, "College 2 Major is too long. 100 Characters Max"),
        techMinor1: Yup.string()
            .optional()
            .max(100, "College 2 Minor is too long. 100 Characters Max"),
        techStart1: Yup.date().max(new Date(4000,12,31), "Tech/Vocational/Business Start Date too large. Max: 12/31/4000)").optional(),
        techEnd1: Yup.date()
            .max(new Date(4000,12,31), "Tech/Vocational/Business End Date too large. Max: 12/31/4000)")
            .optional()
            .min(Yup.ref("techStart1"), "End date can't be before start date"),
        /*     techCreditHours1: Yup.string().matches(
            /^(0|[1-9][0-9]{0,9})$/,
            "Must be a positive number, up to 10 digits",
        ),*/
        techCreditHours1: Yup.string()
            .transform((value, originalValue) =>
                /^\s*0\s*$/.test(originalValue) ? "0" : value,
            )
            .matches(/^[0-9]*$/, "Only numbers allowed.")
            .max(10, "College 2 Name is too long. 10 Characters Max"),
        /*      techCreditHours1: Yup.string()
            .optional()
            .matches(/^[0-9]*$/, "Only numbers allowed.")
            .max(10, "College 2 Name is too long. 10 Characters Max"),*/

        trainingName1: Yup.string().optional(),
        trainingStart1: Yup.date().optional(),
        trainingEnd1: Yup.date()
            .optional()
            .min(
                Yup.ref("trainingStart1"),
                "End date can't be before start date",
            ),
        trainingName2: Yup.string().optional(),
        trainingStart2: Yup.date().optional(),
        trainingEnd2: Yup.date()
            .optional()
            .min(
                Yup.ref("trainingStart2"),
                "End date can't be before start date",
            ),
        trainingName3: Yup.string().optional(),
        trainingStart3: Yup.date().optional(),
        trainingEnd3: Yup.date()
            .optional()
            .min(
                Yup.ref("trainingStart3"),
                "End date can't be before start date",
            ),
        trainingName4: Yup.string().optional(),
        trainingStart4: Yup.date().optional(),
        trainingEnd4: Yup.date()
            .optional()
            .min(
                Yup.ref("trainingStart4"),
                "End date can't be before start date",
            ),
        skillName1: Yup.string().optional(),
        skillName2: Yup.string().optional(),
        skillName3: Yup.string().optional(),
        skillName4: Yup.string().optional(),
        felony: Yup.string().optional(),
        felonyDate: Yup.date().max(new Date(4000,12,31), "Felony Date too large Max: 12/31/4000)").optional(),
        felonyReason: Yup.string()
            .optional()
            .max(
                200,
                "Felony Reason for leaving is too long. 200 Characters Max",
            ),
        misdemeanor: Yup.string().optional(),
        misdemeanorDate: Yup.date().max(new Date(4000,12,31), "Misdemeanor Date too large Max: 12/31/4000)").optional(),
        misdemeanorReason: Yup.string()
            .optional()
            .max(
                200,
                "Felony Reason for leaving is too long. 200 Characters Max",
            ),
        physical: Yup.string().optional(),
        physicalName1: Yup.string()
            .optional()
            .max(250, "Physical Condition is too long. 250 Characters Max"),
        physicalReason1: Yup.string()
            .optional()
            .max(250, "Condition Details is too long. 250 Characters Max"),
        physicalName2: Yup.string().optional().max(250, "Physical Condition 2 is too long. 250 Characters Max"),
        physicalReason2: Yup.string().optional().max(250, "Physical Condition 2 is too long. 250 Characters Max"),
        physicalName3: Yup.string().optional().max(250, "Physical Condition 3 is too long. 250 Characters Max"),
        physicalReason3: Yup.string().optional().max(250, "Physical Condition 3 is too long. 250 Characters Max"),
        relations: Yup.string().optional(),
        relationsName1: Yup.string()
            .optional()
            .max(
                100,
                "Name of Related Employee 1 is too long. 100 Characters Max",
            ),
        relationsDepartment1: Yup.string()
            .optional()
            .max(
                125,
                "Department of Related Employee 1 is too long. 125 Characters Max",
            ),
        relationsRelationship1: Yup.string()
            .optional()
            .max(
                125,
                "Relationship with the Related Employee 1 is too long. 125 Characters Max",
            ),

        relationsName2: Yup.string()
            .optional()
            .max(
                100,
                "Name of Related Employee 2 is too long. 100 Characters Max",
            ),
        relationsDepartment2: Yup.string()
            .optional()
            .max(
                125,
                "Department of Related Employee 2 is too long. 125 Characters Max",
            ),
        relationsRelationship2: Yup.string()
            .optional()
            .max(
                125,
                "Relationship with the Related Employee 2 is too long. 125 Characters Max",
            ),

        employer1: Yup.string()
            .optional()
            .max(150, "Employer 1 Name is too long. 150 Characters Max"),
        employerAddress1: Yup.string()
            .optional()
            .max(200, "Employer 1 Address is too long. 200 Characters Max"),
        employerStart1: Yup.date().max(new Date(4000,12,31), "Employer Start date 1 too large Max: 12/31/4000)").optional(),
        employerEnd1: Yup.date()
            .optional()
            .max(new Date(4000,12,31), "Employer End date 1 too large Max: 12/31/4000)")
            .min(
                Yup.ref("employerStart1"),
                "End date can't be before start date",
            ),
        employerSupervisor1: Yup.string()
            .optional()
            .max(
                150,
                "Employer Supervisor 1 Name is too long. 150 Characters Max",
            ),
        employerJobTitle1: Yup.string()
            .optional()
            .max(
                150,
                "Employer Job Title 1 Name is too long. 150 Characters Max",
            ),
        employerPhone1: Yup.string().optional(),
        employerReason1: Yup.string()
            .optional()
            .max(
                200,
                "Employer 1 Reason for leaving is too long. 200 Characters Max",
            ),
        employerDuties1: Yup.string()
            .optional()
            .max(
                2000,
                "Employer 1 Job description is too long. 2000 Characters Max",
            ),

        employer2: Yup.string()
            .optional()
            .max(150, "Employer 2 Name is too long. 150 Characters Max"),
        employerAddress2: Yup.string()
            .optional()
            .max(200, "Employer 2 Address is too long. 200 Characters Max"),
        employerStart2: Yup.date().max(new Date(4000,12,31), "Employer Start date 2 too large Max: 12/31/4000)").optional(),
        employerEnd2: Yup.date()
            .optional()
            .max(new Date(4000,12,31), "Employer End date 2 too large Max: 12/31/4000)")
            .min(
                Yup.ref("employerStart2"),
                "End date can't be before start date",
            ),
        employerSupervisor2: Yup.string()
            .optional()
            .max(
                150,
                "Employer Supervisor 2 Name is too long. 150 Characters Max",
            ),
        employerJobTitle2: Yup.string()
            .optional()
            .max(
                150,
                "Employer Job Title 2 Name is too long. 150 Characters Max",
            ),
        employerPhone2: Yup.string().optional(),
        employerReason2: Yup.string()
            .optional()
            .max(
                200,
                "Employer 2 Reason for leaving is too long. 200 Characters Max",
            ),
        employerDuties2: Yup.string()
            .optional()
            .max(
                2000,
                "Employer 2 Job description is too long. 2000 Characters Max",
            ),

        employer3: Yup.string()
            .optional()
            .max(150, "Employer 3 Name is too long. 150 Characters Max"),
        employerAddress3: Yup.string()
            .optional()
            .max(200, "Employer 3 Address is too long. 200 Characters Max"),
        employerStart3: Yup.date().max(new Date(4000,12,31), "Employer Start date 3 too large Max: 12/31/4000)").optional(),
        employerEnd3: Yup.date()
            .optional()
            .max(new Date(4000,12,31), "Employer End date 3 too large Max: 12/31/4000)")
            .min(
                Yup.ref("employerStart3"),
                "End date can't be before start date",
            ),
        employerSupervisor3: Yup.string()
            .optional()
            .max(
                150,
                "Employer Supervisor 3 Name is too long. 150 Characters Max",
            ),
        employerJobTitle3: Yup.string()
            .optional()
            .max(
                150,
                "Employer Job Title 3 Name is too long. 150 Characters Max",
            ),
        employerPhone3: Yup.string().optional(),
        employerReason3: Yup.string()
            .optional()
            .max(
                200,
                "Employer 3 Reason for leaving is too long. 200 Characters Max",
            ),
        employerDuties3: Yup.string()
            .optional()
            .max(
                2000,
                "Employer 3 Job description is too long. 2000 Characters Max",
            ),

        employer4: Yup.string()
            .optional()
            .max(150, "Employer 4 Name is too long. 150 Characters Max"),
        employerAddress4: Yup.string()
            .optional()
            .max(200, "Employer 4 Address is too long. 200 Characters Max"),
        employerStart4: Yup.date().max(new Date(4000,12,31), "Employer Start date 4 too large Max: 12/31/4000)").optional(),
        employerEnd4: Yup.date()
            .optional()
            .max(new Date(4000,12,31), "Employer End date 4 too large Max: 12/31/4000)")
            .min(
                Yup.ref("employerStart4"),
                "End date can't be before start date",
            ),
        employerSupervisor4: Yup.string()
            .optional()
            .max(
                150,
                "Employer Supervisor 4 Name is too long. 150 Characters Max",
            ),
        employerJobTitle4: Yup.string()
            .optional()
            .max(
                150,
                "Employer Job Title 4 Name is too long. 150 Characters Max",
            ),
        employerPhone4: Yup.string().optional(),
        employerReason4: Yup.string()
            .optional()
            .max(
                200,
                "Employer 4 Reason for leaving is too long. 200 Characters Max",
            ),
        employerDuties4: Yup.string()
            .optional()
            .max(
                2000,
                "Employer 4 Job description is too long. 2000 Characters Max",
            ),

        employer5: Yup.string()
            .optional()
            .max(150, "Employer 5 Name is too long. 150 Characters Max"),
        employerAddress5: Yup.string()
            .optional()
            .max(200, "Employer 5 Address is too long. 200 Characters Max"),
        employerStart5: Yup.date().max(new Date(4000,12,31), "Employer Start date 5 too large Max: 12/31/4000)").optional(),
        employerEnd5: Yup.date()
            .optional()
            .max(new Date(4000,12,31), "Employer End date 5 too large Max: 12/31/4000)")
            .min(
                Yup.ref("employerStart5"),
                "End date can't be before start date",
            ),
        employerSupervisor5: Yup.string()
            .optional()
            .max(
                150,
                "Employer Supervisor 5 Name is too long. 150 Characters Max",
            ),
        employerJobTitle5: Yup.string()
            .optional()
            .max(
                150,
                "Employer Job Title 5 Name is too long. 150 Characters Max",
            ),
        employerPhone5: Yup.string().optional(),
        employerReason5: Yup.string()
            .optional()
            .max(
                200,
                "Employer 5 Reason for leaving is too long. 200 Characters Max",
            ),
        employerDuties5: Yup.string()
            .optional()
            .max(
                2000,
                "Employer 5 Job description is too long. 2000 Characters Max",
            ),

        employer6: Yup.string()
            .optional()
            .max(150, "Employer 6 Name is too long. 150 Characters Max"),
        employerAddress6: Yup.string()
            .optional()
            .max(200, "Employer 6 Address is too long. 200 Characters Max"),
        employerStart6: Yup.date().max(new Date(4000,12,31), "Employer Start date 6 too large Max: 12/31/4000)").optional(),
        employerEnd6: Yup.date()
            .optional()
            .max(new Date(4000,12,31), "Employer End date 6 too large Max: 12/31/4000)")
            .min(
                Yup.ref("employerStart6"),
                "End date can't be before start date",
            ),
        employerSupervisor6: Yup.string()
            .optional()
            .max(
                150,
                "Employer Supervisor 6 Name is too long. 150 Characters Max",
            ),
        employerJobTitle6: Yup.string()
            .optional()
            .max(
                150,
                "Employer Job Title 6 Name is too long. 150 Characters Max",
            ),
        employerPhone6: Yup.string().optional(),
        employerReason6: Yup.string()
            .optional()
            .max(
                200,
                "Employer 6 Reason for leaving is too long. 200 Characters Max",
            ),
        employerDuties6: Yup.string()
            .optional()
            .max(
                2000,
                "Employer 6 Job description is too long. 2000 Characters Max",
            ),
        employer7: Yup.string()
            .optional()
            .max(150, "Employer 7 Name is too long. 150 Characters Max"),
        employerAddress7: Yup.string()
            .optional()
            .max(200, "Employer 7 Address is too long. 200 Characters Max"),
        employerStart7: Yup.date().max(new Date(4000,12,31), "Employer Start date 7 too large Max: 12/31/4000)").optional(),
        employerEnd7: Yup.date()
            .optional()
            .max(new Date(4000,12,31), "Employer End date 7 too large Max: 12/31/4000)")
            .min(
                Yup.ref("employerStart7"),
                "End date can't be before start date",
            ),
        employerSupervisor7: Yup.string()
            .optional()
            .max(
                150,
                "Employer Supervisor 7 Name is too long. 150 Characters Max",
            ),
        employerJobTitle7: Yup.string()
            .optional()
            .max(
                150,
                "Employer Job Title 7 Name is too long. 150 Characters Max",
            ),
        employerPhone7: Yup.string().optional(),
        employerReason7: Yup.string()
            .optional()
            .max(
                200,
                "Employer 7 Reason for leaving is too long. 200 Characters Max",
            ),
        employerDuties7: Yup.string()
            .optional()
            .max(
                2000,
                "Employer 7 Job description is too long. 2000 Characters Max",
            ),
        employer8: Yup.string()
            .optional()
            .max(150, "Employer 8 Name is too long. 150 Characters Max"),
        employerAddress8: Yup.string()
            .optional()
            .max(200, "Employer 8 Address is too long. 200 Characters Max"),
        employerStart8: Yup.date().max(new Date(4000,12,31), "Employer Start date 8 too large Max: 12/31/4000)").optional(),
        employerEnd8: Yup.date()
            .optional()
            .max(new Date(4000,12,31), "Employer End date 8 too large Max: 12/31/4000)")
            .min(
                Yup.ref("employerStart8"),
                "End date can't be before start date",
            ),
        employerSupervisor8: Yup.string()
            .optional()
            .max(
                150,
                "Employer Supervisor 8 Name is too long. 150 Characters Max",
            ),
        employerJobTitle8: Yup.string()
            .optional()
            .max(
                150,
                "Employer Job Title 8 Name is too long. 150 Characters Max",
            ),
        employerPhone8: Yup.string().optional(),
        employerReason8: Yup.string()
            .optional()
            .max(
                200,
                "Employer 8 Reason for leaving is too long. 200 Characters Max",
            ),
        employerDuties8: Yup.string()
            .optional()
            .max(
                2000,
                "Employer 8 Job description is too long. 2000 Characters Max",
            ),
        employer9: Yup.string()
            .optional()
            .max(150, "Employer 9 Name is too long. 150 Characters Max"),
        employerAddress9: Yup.string()
            .optional()
            .max(200, "Employer 9 Address is too long. 200 Characters Max"),
        employerStart9: Yup.date().max(new Date(4000,12,31), "Employer Start date 9 too large Max: 12/31/4000)").optional(),
        employerEnd9: Yup.date()
            .optional()
            .max(new Date(4000,12,31), "Employer End date 9 too large Max: 12/31/4000)")
            .min(
                Yup.ref("employerStart9"),
                "End date can't be before start date",
            ),
        employerSupervisor9: Yup.string()
            .optional()
            .max(
                150,
                "Employer Supervisor 9 Name is too long. 150 Characters Max",
            ),
        employerJobTitle9: Yup.string()
            .optional()
            .max(
                150,
                "Employer Job Title 9 Name is too long. 150 Characters Max",
            ),
        employerPhone9: Yup.string().optional(),
        employerReason9: Yup.string()
            .optional()
            .max(
                200,
                "Employer 9 Reason for leaving is too long. 200 Characters Max",
            ),
        employerDuties9: Yup.string()
            .optional()
            .max(
                2000,
                "Employer 9 Job description is too long. 2000 Characters Max",
            ),
        employer10: Yup.string()
            .optional()
            .max(150, "Employer 10 Name is too long. 150 Characters Max"),
        employerAddress10: Yup.string()
            .optional()
            .max(200, "Employer 10 Address is too long. 200 Characters Max"),
        employerStart10: Yup.date().max(new Date(4000,12,31), "Employer Start date 10 too large Max: 12/31/4000)").optional(),
        employerEnd10: Yup.date()
            .optional()
            .max(new Date(4000,12,31), "Employer End date 10 too large Max: 12/31/4000)")
            .min(
                Yup.ref("employerStart10"),
                "End date can't be before start date",
            ),
        employerSupervisor10: Yup.string()
            .optional()
            .max(
                150,
                "Employer Supervisor 10 Name is too long. 150 Characters Max",
            ),
        employerJobTitle10: Yup.string()
            .optional()
            .max(
                150,
                "Employer Job Title 10 Name is too long. 150 Characters Max",
            ),
        employerPhone10: Yup.string().optional(),
        employerReason10: Yup.string()
            .optional()
            .max(
                200,
                "Employer 10 Reason for leaving is too long. 200 Characters Max",
            ),
        employerDuties10: Yup.string()
            .optional()
            .max(
                2000,
                "Employer 10 Job description is too long. 2000 Characters Max",
            ),
        veteran: Yup.string().optional(),
        NN_Spouse: Yup.string().optional(),
        NN_SpouseCensus: Yup.string()
            .optional()
            .trim()
            .test(
                "isLengthValid",
                "Census must be at least 6 characters long.",
                (val) => {
                    if (val && val.length === 6) {
                        return val.length === 6 && /^X{4}\d{2}$/.test(val);
                    } else if (val && val.length === 7) {
                        return (
                            val.length <= 7 &&
                            val.replace(/_/g, "").length === 7
                        );
                    }
                    return true;
                },
            ),
        AB_Number: Yup.string().optional(),
        Employment_Status: Yup.string().optional(),
        Position_Title: Yup.string().optional(),

        // Yup.string().optional().test('len', 'Must be exactly 6 characters', val => val && (val.toString().length === 6 )),
    });

    useEffect(() => {
        // dispatch(updateDOB(profile.dob));
        if (profileStatus === "idle") {
            // dispatch(postApplication());
            navigate("/profileCard");
        }
    }, [dispatch, profileStatus, profile, submit, navigate]);

    let content;



    if (profileStatus === "loading") {
        content = (
            <>
                <Row className="m-auto d-flex justify-content-center align-items-center vh-100 vw-100 flex-column">
                    <Spinner
                        className="spinner-style"
                        animation="border"
                        role="status"
                        size="xl"
                    ></Spinner>

                    <Row className="justify-content-center pb-5">
                        <h1 className="text-center">Please wait...</h1>
                    </Row>
                </Row>
            </>
        );
    } else if (profileStatus === "succeeded") {
        content = (
            <>
                <Row className="edit-profile-height d-flex justify-content-center align-content-center text-center " ref={ref}>
                    <Col xs={12} sm={8} className="d-flex align-items-center" >
                        <h2 className="animate__animated animate__fadeIn fw-bolder heading-banner-font">
                            Update Your Profile
                        </h2>
                    </Col>
                </Row>


                <Formik
                    // validateOnMount
                    initialValues={{
                        firstName: profile.firstName ? profile.firstName : "",
                        middleName: profile.middleName
                            ? profile.middleName
                            : "",
                        lastName: profile.lastName ? profile.lastName : "",
                        suffix: profile.suffix ? profile.suffix : "",
                        phoneNumber: profile.phoneNumber
                            ? "+" + profile.phoneNumber.replace(/^\+/, "")
                            : "",

                        dob: profile.dob
                            ? formatDateToInputFormat(profile.dob)
                            : "",
                        gender: profile.gender ? profile.gender : "",
                        addressLine1: profile.addressLine1
                            ? profile.addressLine1
                            : "",
                        addressLine2: profile.addressLine2
                            ? profile.addressLine2
                            : "",
                        city: profile.city ? profile.city : "",
                        state: profile.state ? profile.state : "",
                        zipcode: profile.zipcode ? profile.zipcode : "",
                        navajoTribeMember: profile.navajoTribeMember
                            ? profile.navajoTribeMember
                            : "",
                        census: profile.census ? profile.census : "",
                        nationality: profile.nationality
                            ? profile.nationality
                            : "",
                        hasDriverLicense: profile.hasDriverLicense
                            ? profile.hasDriverLicense
                            : "",
                        driverLicenseNumber: profile.driverLicenseNumber
                            ? profile.driverLicenseNumber
                            : "",
                        driverLicenseClass: profile.driverLicenseClass
                            ? profile.driverLicenseClass
                            : "",
                        driverLicenseState: profile.driverLicenseState
                            ? profile.driverLicenseState
                            : "",
                        driverLicenseExpirationDate:
                            profile.driverLicenseExpirationDate
                                ? formatDateToInputFormat(
                                    profile.driverLicenseExpirationDate,
                                )
                                : "",
                        driverLicenseType: profile.driverLicenseType
                            ? profile.driverLicenseType
                            : "",
                        attendedHighSchool: profile.attendedHighSchool
                            ? profile.attendedHighSchool
                            : "",
                        highSchoolName: profile.highSchoolName
                            ? profile.highSchoolName
                            : "",
                        highSchoolAddress: profile.highSchoolAddress
                            ? profile.highSchoolAddress
                            : "",
                        education: profile.education ? profile.education : "",
                        highSchoolStart: profile.highSchoolStart
                            ? formatDateToInputFormat(profile.highSchoolStart)
                            : "",
                        highSchoolEnd: profile.highSchoolEnd
                            ? formatDateToInputFormat(profile.highSchoolEnd)
                            : "",
                        currentlyEmployed: profile.currentlyEmployed
                            ? profile.currentlyEmployed
                            : "",
                        attendedCollege1: profile.attendedCollege1
                            ? profile.attendedCollege1
                            : "",
                        collegeName1: profile.collegeName1
                            ? profile.collegeName1
                            : "",
                        collegeAddress1: profile.collegeAddress1
                            ? profile.collegeAddress1
                            : "",
                        degree1: profile.degree1 ? profile.degree1 : "",
                        major1: profile.major1 ? profile.major1 : "",
                        minor1: profile.minor1 ? profile.minor1 : "",
                        collegeStart1: profile.collegeStart1
                            ? formatDateToInputFormat(profile.collegeStart1)
                            : "",
                        collegeEnd1: profile.collegeEnd1
                            ? formatDateToInputFormat(profile.collegeEnd1)
                            : "",
                        attendedCollege2: profile.attendedCollege2
                            ? profile.attendedCollege2
                            : "",
                        collegeName2: profile.collegeName2
                            ? profile.collegeName2
                            : "",
                        collegeAddress2: profile.collegeAddress2
                            ? profile.collegeAddress2
                            : "",
                        degree2: profile.degree2 ? profile.degree2 : "",
                        major2: profile.major2 ? profile.major2 : "",
                        minor2: profile.minor2 ? profile.minor2 : "",
                        collegeStart2: profile.collegeStart2
                            ? formatDateToInputFormat(profile.collegeStart2)
                            : "",
                        collegeEnd2: profile.collegeEnd2
                            ? formatDateToInputFormat(profile.collegeEnd2)
                            : "",
                        attendedTechSchool: profile.attendedTechSchool
                            ? profile.attendedTechSchool
                            : "",
                        techSchoolName1: profile.techSchoolName1
                            ? profile.techSchoolName1
                            : "",
                        techSchoolAddress1: profile.techSchoolAddress1
                            ? profile.techSchoolAddress1
                            : "",
                        techDegree1: profile.techDegree1
                            ? profile.techDegree1
                            : "",
                        techMajor1: profile.techMajor1
                            ? profile.techMajor1
                            : "",
                        techMinor1: profile.techMinor1
                            ? profile.techMinor1
                            : "",
                        techStart1: profile.techStart1
                            ? formatDateToInputFormat(profile.techStart1)
                            : "",
                        techEnd1: profile.techEnd1
                            ? formatDateToInputFormat(profile.techEnd1)
                            : "",
                        trainingName1: profile.trainingName1
                            ? profile.trainingName1
                            : "",
                        trainingStart1: profile.trainingStart1
                            ? formatDateToInputFormat(profile.trainingStart1)
                            : "",
                        trainingEnd1: profile.trainingEnd1
                            ? formatDateToInputFormat(profile.trainingEnd1)
                            : "",
                        trainingName2: profile.trainingName2
                            ? profile.trainingName2
                            : "",
                        trainingStart2: profile.trainingStart2
                            ? formatDateToInputFormat(profile.trainingStart2)
                            : "",
                        trainingEnd2: profile.trainingEnd2
                            ? formatDateToInputFormat(profile.trainingEnd2)
                            : "",
                        trainingName3: profile.trainingName3
                            ? profile.trainingName3
                            : "",
                        trainingStart3: profile.trainingStart3
                            ? formatDateToInputFormat(profile.trainingStart3)
                            : "",
                        trainingEnd3: profile.trainingEnd3
                            ? formatDateToInputFormat(profile.trainingEnd3)
                            : "",
                        trainingName4: profile.trainingName4
                            ? profile.trainingName4
                            : "",
                        trainingStart4: profile.trainingStart4
                            ? formatDateToInputFormat(profile.trainingStart4)
                            : "",
                        trainingEnd4: profile.trainingEnd4
                            ? formatDateToInputFormat(profile.trainingEnd4)
                            : "",
                        skillName1: profile.skillName1
                            ? profile.skillName1
                            : "",
                        skillName2: profile.skillName2
                            ? profile.skillName2
                            : "",
                        skillName3: profile.skillName3
                            ? profile.skillName3
                            : "",
                        skillName4: profile.skillName4
                            ? profile.skillName4
                            : "",
                        felony: profile.felony ? profile.felony : "",
                        felonyDate: profile.felonyDate
                            ? profile.felonyDate
                            : "",
                        felonyReason: profile.felonyReason
                            ? profile.felonyReason
                            : "",
                        misdemeanor: profile.misdemeanor
                            ? profile.misdemeanor
                            : "",
                        misdemeanorDate: profile.misdemeanorDate
                            ? profile.misdemeanorDate
                            : "",
                        misdemeanorReason: profile.misdemeanorReason
                            ? profile.misdemeanorReason
                            : "",
                        physical: profile.physical ? profile.physical : "",
                        physicalName1: profile.physicalName1
                            ? profile.physicalName1
                            : "",
                        physicalReason1: profile.physicalReason1
                            ? profile.physicalReason1
                            : "",
                        physicalName2: profile.physicalName2
                            ? profile.physicalName2
                            : "",
                        physicalReason2: profile.physicalReason2
                            ? profile.physicalReason2
                            : "",
                        physicalName3: profile.physicalName3
                            ? profile.physicalName3
                            : "",
                        physicalReason3: profile.physicalReason3
                            ? profile.physicalReason3
                            : "",
                        relations: profile.relations ? profile.relations : "",
                        relationsRelationship1: profile.relationsRelationship1
                            ? profile.relationsRelationship1
                            : "",
                        relationsName1: profile.relationsName1
                            ? profile.relationsName1
                            : "",
                        relationsDepartment1: profile.relationsDepartment1
                            ? profile.relationsDepartment1
                            : "",
                        relationsRelationship2: profile.relationsRelationship2
                            ? profile.relationsRelationship2
                            : "",
                        relationsName2: profile.relationsName2
                            ? profile.relationsName2
                            : "",
                        relationsDepartment2: profile.relationsDepartment2
                            ? profile.relationsDepartment2
                            : "",
                        employer1: profile.employer1 ? profile.employer1 : "",
                        employerAddress1: profile.employerAddress1
                            ? profile.employerAddress1
                            : "",
                        employerStart1: profile.employerStart1
                            ? profile.employerStart1
                            : "",
                        employerEnd1: profile.employerEnd1
                            ? profile.employerEnd1
                            : "",
                        employerSupervisor1: profile.employerSupervisor1
                            ? profile.employerSupervisor1
                            : "",
                        employerJobTitle1: profile.employerJobTitle1
                            ? profile.employerJobTitle1
                            : "",
                        employerPhone1: profile.employerPhone1
                            ? profile.employerPhone1
                            : "",
                        employerReason1: profile.employerReason1
                            ? profile.employerReason1
                            : "",
                        employerDuties1: profile.employerDuties1
                            ? profile.employerDuties1
                            : "",
                        employer2: profile.employer2 ? profile.employer2 : "",
                        employerAddress2: profile.employerAddress2
                            ? profile.employerAddress2
                            : "",
                        employerStart2: profile.employerStart2
                            ? profile.employerStart2
                            : "",
                        employerEnd2: profile.employerEnd2
                            ? profile.employerEnd2
                            : "",
                        employerSupervisor2: profile.employerSupervisor2
                            ? profile.employerSupervisor2
                            : "",
                        employerJobTitle2: profile.employerJobTitle2
                            ? profile.employerJobTitle2
                            : "",
                        employerPhone2: profile.employerPhone2
                            ? profile.employerPhone2
                            : "",
                        employerReason2: profile.employerReason2
                            ? profile.employerReason2
                            : "",
                        employerDuties2: profile.employerDuties2
                            ? profile.employerDuties2
                            : "",
                        employer3: profile.employer3 ? profile.employer3 : "",
                        employerAddress3: profile.employerAddress3
                            ? profile.employerAddress3
                            : "",
                        employerStart3: profile.employerStart3
                            ? profile.employerStart3
                            : "",
                        employerEnd3: profile.employerEnd3
                            ? profile.employerEnd3
                            : "",
                        employerSupervisor3: profile.employerSupervisor3
                            ? profile.employerSupervisor3
                            : "",
                        employerJobTitle3: profile.employerJobTitle3
                            ? profile.employerJobTitle3
                            : "",
                        employerPhone3: profile.employerPhone3
                            ? profile.employerPhone3
                            : "",
                        employerReason3: profile.employerReason3
                            ? profile.employerReason3
                            : "",
                        employerDuties3: profile.employerDuties3
                            ? profile.employerDuties3
                            : "",
                        employer4: profile.employer4 ? profile.employer4 : "",
                        employerAddress4: profile.employerAddress4
                            ? profile.employerAddress4
                            : "",
                        employerStart4: profile.employerStart4
                            ? profile.employerStart4
                            : "",
                        employerEnd4: profile.employerEnd4
                            ? profile.employerEnd4
                            : "",
                        employerSupervisor4: profile.employerSupervisor4
                            ? profile.employerSupervisor4
                            : "",
                        employerJobTitle4: profile.employerJobTitle4
                            ? profile.employerJobTitle4
                            : "",
                        employerPhone4: profile.employerPhone4
                            ? profile.employerPhone4
                            : "",
                        employerReason4: profile.employerReason4
                            ? profile.employerReason4
                            : "",
                        employerDuties4: profile.employerDuties4
                            ? profile.employerDuties4
                            : "",
                        employer5: profile.employer5 ? profile.employer5 : "",
                        employerAddress5: profile.employerAddress5
                            ? profile.employerAddress5
                            : "",
                        employerStart5: profile.employerStart5
                            ? profile.employerStart5
                            : "",
                        employerEnd5: profile.employerEnd5
                            ? profile.employerEnd5
                            : "",
                        employerSupervisor5: profile.employerSupervisor5
                            ? profile.employerSupervisor5
                            : "",
                        employerJobTitle5: profile.employerJobTitle5
                            ? profile.employerJobTitle5
                            : "",
                        employerPhone5: profile.employerPhone5
                            ? profile.employerPhone5
                            : "",
                        employerReason5: profile.employerReason5
                            ? profile.employerReason5
                            : "",
                        employerDuties5: profile.employerDuties5
                            ? profile.employerDuties5
                            : "",
                        employer6: profile.employer6 ? profile.employer6 : "",
                        employerAddress6: profile.employerAddress6
                            ? profile.employerAddress6
                            : "",
                        employerStart6: profile.employerStart6
                            ? profile.employerStart6
                            : "",
                        employerEnd6: profile.employerEnd6
                            ? profile.employerEnd6
                            : "",
                        employerSupervisor6: profile.employerSupervisor6
                            ? profile.employerSupervisor6
                            : "",
                        employerJobTitle6: profile.employerJobTitle6
                            ? profile.employerJobTitle6
                            : "",
                        employerPhone6: profile.employerPhone6
                            ? profile.employerPhone6
                            : "",
                        employerReason6: profile.employerReason6
                            ? profile.employerReason6
                            : "",
                        employerDuties6: profile.employerDuties6
                            ? profile.employerDuties6
                            : "",
                        employer7: profile.employer7 ? profile.employer7 : "",
                        employerAddress7: profile.employerAddress7
                            ? profile.employerAddress7
                            : "",
                        employerStart7: profile.employerStart7
                            ? profile.employerStart7
                            : "",
                        employerEnd7: profile.employerEnd7
                            ? profile.employerEnd7
                            : "",
                        employerSupervisor7: profile.employerSupervisor7
                            ? profile.employerSupervisor7
                            : "",
                        employerJobTitle7: profile.employerJobTitle7
                            ? profile.employerJobTitle7
                            : "",
                        employerPhone7: profile.employerPhone7
                            ? profile.employerPhone7
                            : "",
                        employerReason7: profile.employerReason7
                            ? profile.employerReason7
                            : "",
                        employerDuties7: profile.employerDuties7
                            ? profile.employerDuties7
                            : "",
                        employer8: profile.employer8 ? profile.employer8 : "",
                        employerAddress8: profile.employerAddress8
                            ? profile.employerAddress8
                            : "",
                        employerStart8: profile.employerStart8
                            ? profile.employerStart8
                            : "",
                        employerEnd8: profile.employerEnd8
                            ? profile.employerEnd8
                            : "",
                        employerSupervisor8: profile.employerSupervisor8
                            ? profile.employerSupervisor8
                            : "",
                        employerJobTitle8: profile.employerJobTitle8
                            ? profile.employerJobTitle8
                            : "",
                        employerPhone8: profile.employerPhone8
                            ? profile.employerPhone8
                            : "",
                        employerReason8: profile.employerReason8
                            ? profile.employerReason8
                            : "",
                        employerDuties8: profile.employerDuties8
                            ? profile.employerDuties8
                            : "",
                        employer9: profile.employer9 ? profile.employer9 : "",
                        employerAddress9: profile.employerAddress9
                            ? profile.employerAddress9
                            : "",
                        employerStart9: profile.employerStart9
                            ? profile.employerStart9
                            : "",
                        employerEnd9: profile.employerEnd9
                            ? profile.employerEnd9
                            : "",
                        employerSupervisor9: profile.employerSupervisor9
                            ? profile.employerSupervisor9
                            : "",
                        employerJobTitle9: profile.employerJobTitle9
                            ? profile.employerJobTitle9
                            : "",
                        employerPhone9: profile.employerPhone9
                            ? profile.employerPhone9
                            : "",
                        employerReason9: profile.employerReason9
                            ? profile.employerReason9
                            : "",
                        employerDuties9: profile.employerDuties9
                            ? profile.employerDuties9
                            : "",
                        employer10: profile.employer10 ? profile.employer10 : "",
                        employerAddress10: profile.employerAddress10
                            ? profile.employerAddress10
                            : "",
                        employerStart10: profile.employerStart10
                            ? profile.employerStart10
                            : "",
                        employerEnd10: profile.employerEnd10
                            ? profile.employerEnd10
                            : "",
                        employerSupervisor10: profile.employerSupervisor10
                            ? profile.employerSupervisor10
                            : "",
                        employerJobTitle10: profile.employerJobTitle10
                            ? profile.employerJobTitle10
                            : "",
                        employerPhone10: profile.employerPhone10
                            ? profile.employerPhone10
                            : "",
                        employerReason10: profile.employerReason10
                            ? profile.employerReason10
                            : "",
                        employerDuties10: profile.employerDuties10
                            ? profile.employerDuties10
                            : "",
                        rifDate: profile.rifDate
                            ? formatDateToInputFormat(profile.rifDate)
                            : "",
                        rif: profile.rif ? profile.rif : "",
                        rifFormIn: profile.rifFormIn ? profile.rifFormIn : "",
                        cibFormIn: profile.rifFormIn ? profile.cibFormIn : "",
                        veteran: profile.veteran ? profile.veteran : "",
                        DD214OnFile: profile.DD214OnFile
                            ? profile.DD214OnFile
                            : " ",
                        NN_Spouse: profile.NN_Spouse ? profile.NN_Spouse : "",
                        NN_SpouseAffividavit: profile.NN_SpouseAffividavit
                            ? profile.NN_SpouseAffividavit
                            : "",
                        NN_SpouseCIB: profile.NN_SpouseCIB
                            ? profile.NN_SpouseCIB
                            : "",
                        NN_SpouseLicense: profile.NN_SpouseLicense
                            ? profile.NN_SpouseLicense
                            : "",
                        NN_SpouseCensus: profile.NN_SpouseCensus
                            ? profile.NN_SpouseCensus
                            : "",
                        AB_Number: profile.AB_Number
                            ? profile.AB_Number
                            : "",
                        Employment_Status: profile.Employment_Status
                            ? profile.Employment_Status
                            : "",
                        Position_Title: profile.Position_Title
                            ? profile.Position_Title
                            : "",
                        collegeCreditHours1:
                            profile.collegeCreditHours1 !== null &&
                                profile.collegeCreditHours1 !== undefined
                                ? profile.collegeCreditHours1
                                : "",
                        collegeCreditHours2:
                            profile.collegeCreditHours2 !== null &&
                                profile.collegeCreditHours2 !== undefined
                                ? profile.collegeCreditHours2
                                : "",
                        techCreditHours1:
                            profile.techCreditHours1 !== null &&
                                profile.techCreditHours1 !== undefined
                                ? profile.techCreditHours1
                                : "",

                        // Veteran_DD214Form_OriginalName: profile.Veteran_DD214Form_OriginalName ? profile.Veteran_DD214Form_OriginalName : "",
                        // Veteran_DD214Form_UploadDate: profile.Veteran_DD214Form_UploadDate ? profile.Veteran_DD214Form_UploadDate  : "",
                        // Veteran_DD214Form_FileSize: profile.Veteran_DD214Form_FileSize ? profile.Veteran_DD214Form_FileSize : "",
                        // RIF_Form_OriginalName: profile.RIF_Form_OriginalName ? profile.RIF_Form_OriginalName : "",
                        // RIF_Form_UploadDate: profile.RIF_Form_UploadDate ? profile.RIF_Form_UploadDate : "",
                        // RIF_Form_FileSize: profile.RIF_Form_FileSize ? profile.RIF_Form_FileSize : "",
                        // NN_CIBForm_OriginalName: profile.NN_CIBForm_OriginalName ? profile.NN_CIBForm_OriginalName : "",
                        // NN_CIBForm_UploadDate: profile.NN_CIBForm_UploadDate ? profile.NN_CIBForm_UploadDate : "",
                        // NN_CIBForm_FileSize: profile.NN_CIBForm_FileSize ? profile.NN_CIBForm_FileSize : "",
                    }}
                    validate={(values) => {
                        const errors = {};
                        const requiredMessage = " ";

                        const requiredFields = {
                            employer1: {
                                fields: [
                                    "employer1",
                                    "employerAddress1",
                                    "employerDuties1",
                                    "employerJobTitle1",
                                    "employerPhone1",
                                    "employerReason1",
                                    "employerSupervisor1",
                                    "employerEnd1",
                                    "employerStart1",
                                ],
                            },

                            employer2: {
                                fields: [
                                    "employer2",
                                    "employerAddress2",
                                    "employerDuties2",
                                    "employerJobTitle2",
                                    "employerPhone2",
                                    "employerReason2",
                                    "employerSupervisor2",
                                    "employerEnd2",
                                    "employerStart2",
                                ],
                            },

                            employer3: {
                                fields: [
                                    "employer3",
                                    "employerAddress3",
                                    "employerDuties3",
                                    "employerJobTitle3",
                                    "employerPhone3",
                                    "employerReason3",
                                    "employerSupervisor3",
                                    "employerEnd3",
                                    "employerStart3",
                                ],
                            },

                            employer4: {
                                fields: [
                                    "employer4",
                                    "employerAddress4",
                                    "employerDuties4",
                                    "employerJobTitle4",
                                    "employerPhone4",
                                    "employerReason4",
                                    "employerSupervisor4",
                                    "employerEnd4",
                                    "employerStart4",
                                ],
                            },

                            employer5: {
                                fields: [
                                    "employer5",
                                    "employerAddress5",
                                    "employerDuties5",
                                    "employerJobTitle5",
                                    "employerPhone5",
                                    "employerReason5",
                                    "employerSupervisor5",
                                    "employerEnd5",
                                    "employerStart5",
                                ],
                            },

                            employer6: {
                                fields: [
                                    "employer6",
                                    "employerAddress6",
                                    "employerDuties6",
                                    "employerJobTitle6",
                                    "employerPhone6",
                                    "employerReason6",
                                    "employerSupervisor6",
                                    "employerEnd6",
                                    "employerStart6",
                                ],
                            },

                            employer7: {
                                fields: [
                                    "employer7",
                                    "employerAddress7",
                                    "employerDuties7",
                                    "employerJobTitle7",
                                    "employerPhone7",
                                    "employerReason7",
                                    "employerSupervisor7",
                                    "employerEnd7",
                                    "employerStart7",
                                ],
                            },
                            employer8: {
                                fields: [
                                    "employer8",
                                    "employerAddress8",
                                    "employerDuties8",
                                    "employerJobTitle8",
                                    "employerPhone8",
                                    "employerReason8",
                                    "employerSupervisor8",
                                    "employerEnd8",
                                    "employerStart8",
                                ],
                            },
                            employer9: {
                                fields: [
                                    "employer9",
                                    "employerAddress9",
                                    "employerDuties9",
                                    "employerJobTitle9",
                                    "employerPhone9",
                                    "employerReason9",
                                    "employerSupervisor9",
                                    "employerEnd9",
                                    "employerStart9",
                                ],
                            },
                            employer10: {
                                fields: [
                                    "employer10",
                                    "employerAddress10",
                                    "employerDuties10",
                                    "employerJobTitle10",
                                    "employerPhone10",
                                    "employerReason10",
                                    "employerSupervisor10",
                                    "employerEnd10",
                                    "employerStart10",
                                ],
                            },

                            driverLicense: {
                                fields: [
                                    "driverLicenseNumber",
                                    "driverLicenseClass",
                                    "driverLicenseState",
                                    "driverLicenseExpirationDate",
                                    "driverLicenseType",
                                ],
                            },
                            highSchool: {
                                fields: [
                                    "highSchoolName",
                                    "highSchoolAddress",
                                    "highSchoolStart",
                                    "highSchoolEnd",
                                    "education",
                                ],
                            },
                            college1: {
                                fields: [
                                    "collegeName1",
                                    "collegeStart1",
                                    "collegeEnd1",
                                    "collegeAddress1",
                                    "degree1",
                                    "major1",
                                    "collegeCreditHours1",
                                ],
                            },
                            college2: {
                                fields: [
                                    "collegeName2",
                                    "collegeStart2",
                                    "collegeEnd2",
                                    "collegeAddress2",
                                    "degree2",
                                    "major2",
                                    "collegeCreditHours2",
                                ],
                            },
                            techSchool: {
                                fields: [
                                    "techSchoolName1",
                                    "techSchoolAddress1",
                                    "techDegree1",
                                    "techMajor1",
                                    "techStart1",
                                    "techEnd1",
                                    "techCreditHours1",
                                ],
                            },
                            felony: {
                                fields: ["felonyDate", "felonyReason"],
                            },
                            misdemeanor: {
                                fields: [
                                    "misdemeanorDate",
                                    "misdemeanorReason",
                                ],
                            },
                            physical: {
                                fields: ["physicalName1", "physicalReason1"],
                            },
                            physical2: {
                                fields: ["physicalName2", "physicalReason2"],
                            },
                            physical3: {
                                fields: ["physicalName3", "physicalReason3"],
                            },
                            relations: {
                                fields: [
                                    "relationsName1",
                                    "relationsDepartment1",
                                    "relationsRelationship1",
                                ],
                            },
                            relations2: {
                                fields: [
                                    "relationsName2",
                                    "relationsDepartment2",
                                    "relationsRelationship2",
                                ],
                            },
                        };
                        const isAnyFieldFilled = (categoryValues) =>
                            categoryValues.some(
                                (field) =>
                                    values[field] !== "" ||
                                    values[field] === "Yes",
                            );
                        const addRequiredErrors = (categoryValues) =>
                            categoryValues.forEach((field) => {
                                if (!values[field]) {
                                    errors[field] = requiredMessage;
                                }
                            });

                        Object.entries(requiredFields).forEach(
                            ([category, { fields, iterations }]) => {
                                if (!iterations && isAnyFieldFilled(fields)) {
                                    addRequiredErrors(fields);
                                }

                                if (
                                    category === "driverLicense" &&
                                    values.hasDriverLicense === "Yes"
                                ) {
                                    addRequiredErrors(fields);
                                    /*if (
                                        !iterations &&
                                        isAnyFieldFilled(fields)
                                    ) {
                                        addRequiredErrors(fields);
                                    }
                                    if (
                                        !(
                                            values.driverLicenseNumber &&
                                            values.driverLicenseClass &&
                                            values.driverLicenseState &&
                                            values.driverLicenseExpirationDate &&
                                            values.driverLicenseType
                                        )
                                    ) {
                                        console.log("NO VALUES");
                                        addRequiredErrors(fields);
                                    }*/
                                }
                                if (
                                    category === "felony" &&
                                    values.felony === "Yes"
                                ) {
                                    addRequiredErrors(fields);
                                }
                                if (
                                    category === "misdemeanor" &&
                                    values.misdemeanor === "Yes"
                                ) {
                                    addRequiredErrors(fields);
                                }
                                if (
                                    category === "physical" &&
                                    values.physical === "Yes"
                                ) {
                                    addRequiredErrors(fields);
                                }
                                if (
                                    category === "highSchool" &&
                                    values.attendedHighSchool === "Yes"
                                ) {
                                    addRequiredErrors(fields);
                                }
                                if (
                                    category === "college1" &&
                                    values.attendedCollege1 === "Yes"
                                ) {
                                    addRequiredErrors(fields);
                                }
                                if (
                                    category === "college2" &&
                                    values.attendedCollege2 === "Yes"
                                ) {
                                    addRequiredErrors(fields);
                                }
                                if (
                                    category === "techSchool" &&
                                    values.attendedTechSchool === "Yes"
                                ) {
                                    addRequiredErrors(fields);
                                }

                                if (iterations) {
                                    for (let i = 1; i <= iterations; i++) {
                                        let anyFieldFilled = false;
                                        fields.forEach((field) => {
                                            if (values[`${field}${i}`] !== "") {
                                                anyFieldFilled = true;
                                            }
                                        });

                                        if (anyFieldFilled) {
                                            fields.forEach((field) => {
                                                if (!values[`${field}${i}`]) {
                                                    errors[`${field}${i}`] =
                                                        requiredMessage;
                                                }
                                            });
                                        }
                                    }
                                }
                            },
                        );

                        return errors;
                    }}
                    validationSchema={signInValidationSchema}
                    onSubmit={async (values, { setSubmitting, isValid }) => {
                        try {
                            // console.log("SENT values: ", values);
                            // Remove DD214OnFile from values,
                            // It's a calculated field in FM
                            delete values.DD214OnFile;
                            delete values.NN_SpouseAffividavit;
                            delete values.NN_SpouseCIB;
                            delete values.NN_SpouseLicense;

                            // CIBs are censored on the backend and sent as XXXX12
                            if (
                                values.census &&
                                values.census.startsWith("XXXX")
                            ) {
                                values.census = "same";
                            }

                            // CIBs are censored on the backend and sent as XXXX12
                            if (
                                values.NN_SpouseCensus &&
                                values.NN_SpouseCensus.startsWith("XXXX")
                            ) {
                                values.NN_SpouseCensus = "same";
                            }

                            // console.log("delete promises: ", deletePromises);
                            // Wait for delete threads to finish
                            if (deletePromises.length > 0) {
                                // console.log("DELETE starting to wait");
                                Promise.allSettled(uploadPromises).then(
                                    (values) => console.log(values),
                                );
                                // console.log("DELETE finished waiting");
                                dispatch(resetDeleteUploadFile());
                                // console.log("DELETE promises deleted");
                            }

                            // Wait for uploads threads to finish
                            // console.log("upload promises: ", uploadPromises);
                            if (uploadPromises.length > 0) {
                                // setIsSubmitting(true)
                                // console.log("UPLOAD starting to wait");
                                Promise.allSettled(uploadPromises).then(
                                    (values) => console.log(values),
                                );
                                // console.log("UPLOAD finished waiting");
                                dispatch(resetUploadFile());
                                // console.log("UPLOAD promises deleted");
                            }

                            // console.log("updating profile...");
                            let response = await dispatch(postProfile(values));

                            if (response?.payload?.message === "Success") {
                                await Swal.fire({
                                    title: "Your changes have been saved",
                                    icon: "success",
                                    showConfirmButton: false,
                                    timer: 1800,
                                });
                            }
                        } catch (error) {
                            await Swal.fire({
                                icon: "error",
                                title: "Oops...",
                                text: "Failed to save data!",
                                showConfirmButton: false,
                                timer: 1800,
                            });
                            console.error(error);
                            return Promise.reject(error);
                        }

                        if (step !== 5) {
                            nextStep();
                        } if (step === 5) {
                            if(profileComplete){
                                navigate("/applicationSubmit")
                            }
                        }

                        await dispatch(fetchProfile());
                        dispatch(fetchProfileStatus())
                        setSubmitting(false);
                    }}
                >
                    {(props) => (
                        <Form onSubmit={props.handleSubmit}>
                            <Col className="steps mt-5 margin-btm " >
                                <div
                                    className={`step ${arePersonalInfoFieldsFilled(
                                        props.values,
                                    ) && step === 1
                                        ? "complete pulse"
                                        : step === 1
                                            ? "step-active pulse"
                                            : arePersonalInfoFieldsFilled(
                                                props.values,
                                            )
                                                ? "complete"
                                                : "not-active "
                                        }`}
                                    onClick={() => goToStep(1)}
                                /*    onClick={() => {

                                    if (Object.keys(props.errors).length === 0) {
                                        props.handleSubmit();
                                        goToStep(1);
                                    }
                                }}*/
                                >
                                    <FontAwesomeIcon
                                        icon={faUserCircle}
                                        className={`step-icon  ${step === 1 &&
                                            arePersonalInfoFieldsFilled(
                                                props.values,
                                            )
                                            ? "icon-color"
                                            : "icon-grey"
                                            }`}
                                        size="2x"
                                    />
                                    <span className="step-text">
                                        Personal Information
                                    </span>
                                    {arePersonalInfoFieldsFilled(
                                        props.values,
                                    ) ? (
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            className="checkmark no-pulse"
                                            size={"1x"}
                                        />
                                    ) : (
                                        <>
                                            <FontAwesomeIcon
                                                icon={faExclamationTriangle}
                                                className="attention-icon pulse-attention"
                                                size={"1x"}
                                                style={{
                                                    color: "orange",
                                                    textShadow: "0 0 2px #000",
                                                }}
                                            />
                                        </>
                                    )}
                                </div>

                                <div
                                    className={`step ${areDriverLicenseFieldsFilled(
                                        props.values,
                                    ) && step === 2
                                        ? "complete pulse"
                                        : step === 2
                                            ? "step-active pulse"
                                            : areDriverLicenseFieldsFilled(
                                                props.values,
                                            )
                                                ? "complete"
                                                : "not-active "
                                        }`}
                                    onClick={() => goToStep(2)}
                                /*     onClick={() => {

                                    if (Object.keys(props.errors).length === 0) {

                                        props.handleSubmit();
                                        goToStep(2);
                                    }
                                }}*/
                                >
                                    <FontAwesomeIcon
                                        icon={faIdCard}
                                        className={`step-icon  ${step === 2 &&
                                            areDriverLicenseFieldsFilled(
                                                props.values,
                                            )
                                            ? "icon-color"
                                            : "icon-grey"
                                            }`}
                                        size="2x"
                                    />
                                    <span className="step-text">
                                        Driver License
                                    </span>
                                    {areDriverLicenseFieldsFilled(
                                        props.values,
                                    ) ? (
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            className="checkmark no-pulse"
                                            size={"1x"}
                                        />
                                    ) : (
                                        <FontAwesomeIcon
                                            icon={faExclamationTriangle}
                                            className="icon-attention-color  pulse"
                                            size={"1x"}
                                        />
                                    )}
                                </div>
                                <div
                                    className={`step ${areEducationFieldsFilled(
                                        props.values,
                                    ) && step === 3
                                        ? "complete pulse"
                                        : step === 3
                                            ? "step-active pulse"
                                            : areEducationFieldsFilled(
                                                props.values,
                                            )
                                                ? "complete"
                                                : "not-active "
                                        }`}
                                    onClick={() => goToStep(3)}
                                /*        onClick={() => {

                                    if (Object.keys(props.errors).length === 0) {

                                        props.handleSubmit();
                                        goToStep(3);
                                    }
                                }}*/
                                >
                                    <FontAwesomeIcon
                                        icon={faGraduationCap}
                                        className={`step-icon  ${step === 3 &&
                                            areEducationFieldsFilled(
                                                props.values,
                                            )
                                            ? "icon-color"
                                            : "icon-grey"
                                            }`}
                                        size="2x"
                                    />
                                    <span className="step-text">
                                        Education Details
                                    </span>
                                    {areEducationFieldsFilled(props.values) ? (
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            className="checkmark no-pulse"
                                            size={"1x"}
                                        />
                                    ) : (
                                        <FontAwesomeIcon
                                            icon={faExclamationTriangle}
                                            className="icon-attention-color pulse"
                                            size={"1x"}
                                        />
                                    )}
                                </div>

                                <div
                                    className={`step ${areEmploymentFieldsFilled(
                                        props.values,
                                    ) && step === 4
                                        ? "complete pulse"
                                        : step === 4
                                            ? "step-active pulse"
                                            : areEmploymentFieldsFilled(
                                                props.values,
                                            )
                                                ? "complete"
                                                : "not-active "
                                        }`}
                                    onClick={() => goToStep(4)}
                                /*  onClick={() => {

                                    if (Object.keys(props.errors).length === 0) {

                                        props.handleSubmit();
                                        goToStep(4);
                                    }
                                }}*/
                                >
                                    <FontAwesomeIcon
                                        icon={faBriefcase}
                                        className={`step-icon  ${step === 4 &&
                                            areEmploymentFieldsFilled(
                                                props.values,
                                            )
                                            ? "icon-color"
                                            : "icon-grey"
                                            }`}
                                        size="2x"
                                    />
                                    <span className="step-text">
                                        Employment History
                                    </span>
                                    {areEmploymentFieldsFilled(props.values) ? (
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            className="checkmark no-pulse"
                                            size={"1x"}
                                        />
                                    ) : (
                                        <>
                                            <FontAwesomeIcon
                                                icon={faExclamationTriangle}
                                                className="icon-attention-color pulse"
                                                size={"1x"}
                                            />
                                        </>
                                    )}
                                </div>

                                <div
                                    className={`step ${areAdditionalFieldsFilled(
                                        props.values,
                                    ) && step === 5
                                        ? "complete pulse"
                                        : step === 5
                                            ? "step-active pulse "
                                            : areAdditionalFieldsFilled(
                                                props.values,
                                            )
                                                ? "complete"
                                                : "not-active "
                                        }`}
                                    onClick={() => {
                                        goToStep(5);
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        // onClick={() =>{ goToStep(5)}}
                                        className={`step-icon  ${step === 5 &&
                                            areAdditionalFieldsFilled(
                                                props.values,
                                            )
                                            ? "icon-color "
                                            : "icon-grey"
                                            }`}
                                        size="2x"
                                    />
                                    <span className="step-text">
                                        Additional Info
                                    </span>
                                    {areAdditionalFieldsFilled(props.values) ? (
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            className="checkmark no-pulse"
                                            size={"1x"}
                                        />
                                    ) : (
                                        <>
                                            <FontAwesomeIcon
                                                icon={faExclamationTriangle}
                                                className="icon-attention-color pulse"
                                                size={"1x"}
                                            />
                                        </>
                                    )}
                                </div>
                                <div
                                    className={`step not-active ${profileComplete && "complete pulse"}}`}
                                    onClick={() => {
                                        if (profileComplete) {
                                            navigate("/applicationSubmit")
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faRightToBracket}
                                        className={`step-icon  ${profileComplete
                                            ? "icon-color"
                                            : "icon-grey"
                                            }`}
                                        // className="icon-color"
                                        size="2x"
                                    />
                                    <span className="step-text">
                                        Apply Now
                                    </span>
                                    {profileComplete ? (
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            className="checkmark no-pulse"
                                            size={"1x"}
                                        />
                                    ) : (
                                        <FontAwesomeIcon
                                            icon={faExclamationTriangle}
                                            className="icon-attention-color pulse"
                                            size={"1x"}
                                        />
                                    )}
                                </div>

                            </Col>
                            <Step />
                            <div className={` ${anyErrors(props.errors)
                                ? "error-notification"
                                : ""

                                }`}>
                                {Object.keys(props.errors).length > 0 ? (
                                    (() => {
                                        const fieldElements = [];
                                        const fieldsData = [
                                            {
                                                fields: personalInfoFieldsMap,
                                                check: true,
                                            },

                                            {
                                                fields: employmentFieldsMap,
                                                check: true,
                                            },
                                        ];

                                        if (
                                            props.values.hasDriverLicense === "Yes"
                                        ) {
                                            fieldsData.push({
                                                fields: driverLicenseFieldsMap,
                                                check: true,
                                            });
                                        }
                                        if (props.values.felony === "Yes") {
                                            fieldsData.push({
                                                fields: felonyFieldsMap,
                                                check: true,
                                            });
                                        }
                                        if (props.values.misdemeanor === "Yes") {
                                            fieldsData.push({
                                                fields: misdemeanorFieldsMap,
                                                check: true,
                                            });
                                        }
                                        if (props.values.physical === "Yes") {
                                            fieldsData.push({
                                                fields: physical1FieldsMap,
                                                check: true,
                                            });

                                        }
                                        if (props.values.relations === "Yes") {
                                            fieldsData.push({
                                                fields: relations1FieldsMap,
                                                check: true,
                                            });
                                        }
                                        if (
                                            props.values.attendedHighSchool ===
                                            "Yes"
                                        ) {
                                            fieldsData.push({
                                                fields: highSchoolFieldsMap,
                                                check: true,
                                            });
                                        }
                                        if (
                                            props.values.attendedCollege1 === "Yes"
                                        ) {
                                            fieldsData.push({
                                                fields: college1FieldsMap,
                                                check: true,
                                            });
                                        }
                                        if (
                                            props.values.attendedCollege2 === "Yes"
                                        ) {
                                            fieldsData.push({
                                                fields: college2FieldsMap,
                                                check: true,
                                            });
                                        }
                                        if (
                                            props.values.attendedTechSchool ===
                                            "Yes"
                                        ) {
                                            fieldsData.push({
                                                fields: techSchoolFieldsMap,
                                                check: true,
                                            });
                                        }

                                        fieldsData.forEach(({ fields, check }) => {
                                            const omittedFields = checkFields(
                                                props.values,
                                                fields,
                                                check,
                                            );

                                            if (omittedFields !== true) {
                                                omittedFields.forEach(
                                                    (fieldName) => {
                                                        const errorMessage =
                                                            props.errors[fieldName];
                                                        if (errorMessage) {
                                                            fieldElements.push(
                                                                <p key={fieldName}>
                                                                    <span
                                                                        className="link-style"
                                                                        onClick={handleFieldClick(fieldName)}
                                                                    >
                                                                        {fields[fieldName] || fieldName} {errorMessage}
                                                                    </span>
                                                                </p>,
                                                            );
                                                        }
                                                    },
                                                );
                                            }
                                        });
                                        Object.entries(props.errors).forEach(
                                            ([fieldName, errorMessage]) => {
                                                if (
                                                    !fieldElements.find(
                                                        (element) =>
                                                            element.key ===
                                                            fieldName,
                                                    )
                                                ) {
                                                    fieldElements.push(
                                                        <p key={fieldName}>
                                                            <span
                                                                className="link-style"
                                                                onClick={handleFieldClick(fieldName)}
                                                            >
                                                                {fieldName}
                                                                {errorMessage}
                                                            </span>
                                                        </p>,
                                                    );
                                                }
                                            },
                                        );
                                        if (fieldElements.length > 0) {
                                            return (
                                                <div
                                                    className={`d-flex flex-column justify-content-center align-items-center mt-3 animate__animated ${shouldShake
                                                        ? "animate__headShake animate__faster"
                                                        : ""
                                                        }`}
                                                    onAnimationEnd={() =>
                                                        setShouldShake(false)
                                                    }
                                                >
                                                    <div>
                                                        <p className="text-danger">
                                                            Please complete the
                                                            following fields before
                                                            proceeding:
                                                        </p>
                                                        <p
                                                            className="text-muted text-center"
                                                            style={{
                                                                fontSize: "0.8em",
                                                            }}
                                                        >
                                                            (Click on a field to be
                                                            directed to it)
                                                        </p>
                                                    </div>
                                                    {fieldElements}
                                                </div>
                                            );
                                        }

                                        return null;
                                    })()
                                ) : (
                                    <></>
                                )}
                            </div>

                            <Row className="d-flex justify-content-center">
                                {/* <Toast>
                                    <Toast.Header>
                                        <img src="holder.js/20x20?text=%20" className="rounded me-2" alt="" />
                                        <strong className="me-auto">Bootstrap</strong>
                                        <small>11 mins ago</small>
                                    </Toast.Header>
                                    <Toast.Body>Hello, world! This is a toast message.</Toast.Body>
                                </Toast> */}
                                <p className="text-muted w-auto fst-italic fs-5 fw-bold text-center">
                                    NOTE: Only the provided information entered into the form will be used during the qualification/evaluation process.
                                    <br />
                                    All the uploaded documents, including resumes, will only be utilized for additional verification of the information entered.
                                </p>
                            </Row>

                            <Row>
                                <Col className="d-flex justify-content-center gap-5 mb-5">
                                    {step > 1 && (
                                        <Button
                                            variant="outline-secondary"
                                            type="button"
                                            onClick={prevStep}
                                        >
                                            <ArrowLeft /> Previous
                                        </Button>
                                    )}
                                    {step < 5 && (
                                        <Button variant="success" type="submit">
                                            Save & Continue
                                        </Button>
                                    )}

                                    {step === 5 && (
                                        <Button variant="success" type="submit">
                                            Save & Continue to Application
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        </Form>
                    )}
                </Formik >
            </>
        );
    } else if (profileStatus === "failed") {
        toast.error("Error loading. Try again Later.", {
            type: "error",
            isLoading: false,
            theme: "colored",
            autoClose: "3500",
            containerId: "root",
            position: "top-center",
        });
        content = (
            <>
                <Container className="bg-dark-subtle bg-opacity-25  rounded-5 border-shadow">
                    <Row>
                        <Card>
                            <Card.Body>
                                <Card.Title>
                                    <b>
                                        Failed to load profile. Refresh the page
                                        or try again later.
                                    </b>
                                </Card.Title>
                            </Card.Body>
                        </Card>
                    </Row>
                </Container>
            </>
        );
    }

    return <> {content}</>;
};
